// Active-grid crop for spec_mod.
//
// The strand grid the hair decoders are authored at is heavily over-padded: the
// active S*N cells sit in a padded [s_pad, n_pad] grid (for Isaac: 36618*5 =
// 183090 active in 65536*32 = 2.1M, 8.7% used). mat_mod is a ConvTranspose
// ladder whose strides derive from (s_pad, n_pad), so it CANNOT shrink without a
// retrain — but it runs once at load, so its waste is paid once. spec_mod runs
// per settled frame and is a pure LOCAL conv (4x4, down s(2,1) -> up s(2,1))
// with no s_pad/n_pad in its architecture, so it is size-agnostic: feeding it a
// tight grid gives a BIT-EXACT active output at ~1/7 the compute.
//
// Two conditions make the cropped output exact:
//   - the active cells are contiguous from the origin with zero padding after, so
//     no active cell's 4x4 receptive field reads a cell the crop dropped;
//   - the grid keeps enough margin for the kernel — sSpec rounds S up to a
//     multiple of 256 (ample stride-2 margin), nSpec = N + 3 (the 4x4 kernel's
//     N-margin; N=5 -> 8 verified bit-exact, N+2 is not).
//
// Whether the crop is legal at runtime is a property of the LOADED ASSET, not a
// flag: only a spec_mod exported with symbolic (S, N) axes can take a grid other
// than the one it was traced at. hair-texavatars `ac46d55` made the export
// dynamic; packs built before it have hardcoded dims and must keep the full grid.
// isDynamicSpecGrid probes for that, so an old pack degrades to the padded grid
// instead of failing at run time.

// Round S up to this multiple for the cropped grid — an even sSpec with ample
// margin for spec_mod's stride-2 S-axis convs.
const S_QUANTUM = 256;
// spec_mod's 4x4 kernel needs 3 cells of N-margin past the active N.
const N_MARGIN = 3;

/**
 * Does the loaded spec_mod accept a dynamic (S, N) grid?
 *
 * ORT exposes a symbolic dimension as a STRING in `inputMetadata[i].shape`
 * (a number is a fixed dim). A model exported with `dynamic_axes={"spec_in":
 * {2: "S", 3: "N"}}` therefore reports [1, 40, "S", "N"].
 *
 * @param inputMetadata  session.inputMetadata (ORT >= 1.21); undefined on older
 *                       builds → false (keep the full grid).
 * @param name           input to look for; falls back to the first tensor input.
 */
export function isDynamicSpecGrid(inputMetadata, name = 'spec_in') {
  if (!Array.isArray(inputMetadata) || inputMetadata.length === 0) return false;
  const tensors = inputMetadata.filter((m) => m && m.isTensor !== false && Array.isArray(m.shape));
  const meta = tensors.find((m) => m.name === name) ?? tensors[0];
  if (!meta || meta.shape.length !== 4) return false;
  // Only the two spatial axes matter — batch/channel stay fixed.
  return typeof meta.shape[2] === 'string' && typeof meta.shape[3] === 'string';
}

/**
 * The grid to run spec_mod on.
 *
 * @param dims  { S, N, sPad, nPad } from the hair manifest.
 * @param crop  true once the loaded asset is known to take dynamic dims.
 * @returns { sG, nG } — the cropped active grid when `crop`, else the padded grid.
 *
 * Both axes clamp to the padded grid: the crop only ever shrinks. A manifest
 * whose margin would exceed the pad (tiny S, or n_pad already == N+3) falls back
 * to that axis's padded size rather than asking mat_mod for cells it never wrote.
 */
export function activeSpecGrid({ S, N, sPad, nPad }, crop) {
  if (!crop) return { sG: sPad, nG: nPad };
  return {
    sG: Math.min(Math.ceil(S / S_QUANTUM) * S_QUANTUM, sPad),
    nG: Math.min(N + N_MARGIN, nPad),
  };
}
